import React from "react";
import {
  FiActivity,
  FiArrowDown,
  FiArrowUp,
  FiBell,
  FiBookOpen,
  FiCalendar,
  FiCamera,
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
  FiCopy,
  FiDroplet,
  FiFileText,
  FiGlobe,
  FiImage,
  FiMic,
  FiMinus,
  FiPhone,
  FiPhoneOff,
  FiPlay,
  FiPlus,
  FiRadio,
  FiTv,
  FiUser,
  FiVideo,
  FiVolume2,
  FiWifi,
  FiX,
  FiZap,
} from "react-icons/fi";
import {
  FaBasketShopping,
  FaBrain,
  FaChair,
  FaChild,
  FaChildReaching,
  FaCouch,
  FaDumbbell,
  FaEarthEurope,
  FaEye,
  FaFlag,
  FaFont,
  FaPersonWalking,
  FaTableCells,
  FaTableCellsLarge,
  FaTrophy,
} from "react-icons/fa6";
import { usePublicLang } from "../../../config/i18n/public";

/* Drawn stand-ins for the app screenshots, for the colour redesign.
 *
 * The real screenshots show the app as it ships today — orange on beige, with
 * a differently tinted icon for every activity. These redraw the same screens
 * the way the redesign proposes them: cream-white ground, the two brand
 * typefaces, palette colours only, and every icon on the same silver tile.
 *
 * They are design proposals, not the app. Everything is sized in `cqw`, so a
 * phone scales as one piece with whatever box it is given.
 */

/* ------------------------------------------------------------- primitives */
const Tile = ({ Icon, children }) => (
  <span className="phone-icon">{Icon ? <Icon /> : children}</span>
);

function StepBar({ full = false }) {
  return (
    <div className="phone-bar">
      <span className="phone-round">
        <FiChevronLeft />
      </span>
      <span className={`phone-progress ${full ? "is-full" : ""}`} />
      <span className="phone-round">
        <FiX />
      </span>
    </div>
  );
}

const TABS = [
  { id: "profile", Icon: FiUser, label: "Profil" },
  { id: "routines", Icon: FiCopy, label: "Routinen" },
  { id: "feed", Icon: FiImage, label: "Feed" },
  { id: "calendar", Icon: FiCalendar, label: "Kalender" },
  { id: "reminders", Icon: FiBell, label: "Erinnerungen" },
];

function TabBar({ active }) {
  return (
    <div className="phone-tabs">
      {TABS.map(({ id, Icon, label }) => (
        <span key={id} className={id === active ? "is-active" : ""}>
          <Icon />
          {label}
        </span>
      ))}
    </div>
  );
}

function BackRow() {
  return (
    <div className="phone-back">
      <span className="phone-pill">
        <FiChevronLeft /> Zurück
      </span>
      <span>Gesundheit</span>
    </div>
  );
}

function Ring({ value, tone = "accent" }) {
  return (
    <span className={`phone-ring phone-ring--${tone}`} style={{ "--v": value }}>
      <span>
        <b>{value}</b>
        von 100
      </span>
    </span>
  );
}

const Spark = ({ points }) => (
  <svg className="phone-spark" viewBox="0 0 40 16" aria-hidden>
    <polyline points={points} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* --------------------------------------------------------------- pickers */
const PICK = {
  de: {
    eyebrow: "Neue Routine",
    title: "Welche Aktivität?",
    intro: "Wähle, was SteffiCare regelmäßig anbieten soll.",
    groups: [
      {
        tone: "sport",
        label: "Sport",
        count: 7,
        items: [
          { Icon: FaChair, label: "Sitzübung" },
          { Icon: FaDumbbell, label: "Schultern & Arme" },
          { Icon: FaChildReaching, label: "Arme & Koordination" },
          { Icon: FaPersonWalking, label: "Beine & Balance" },
          { Icon: FaCouch, label: "Hüfte & Aufstehen" },
          { Icon: FaChild, label: "Ganzkörper-Mix" },
        ],
      },
      {
        tone: "mind",
        label: "Gehirnjogging",
        count: 9,
        items: [
          { Icon: FaBasketShopping, label: "Einkaufskorb-Memory" },
          { Icon: FaEye, label: "Einkaufstablett" },
          { Icon: FaTableCellsLarge, label: "Verbindungen" },
          { Icon: FaFont, label: "Wordle" },
          { Icon: FaTableCells, label: "Kreuzworträtsel" },
          { Icon: FaFlag, label: "Flaggen raten" },
          { Icon: FaEarthEurope, label: "Länder raten" },
          { Icon: FaTrophy, label: "Millionenshow" },
        ],
      },
    ],
  },
  en: {
    eyebrow: "New routine",
    title: "Which activity?",
    intro: "Choose what SteffiCare should offer regularly.",
    groups: [
      {
        tone: "sport",
        label: "Exercise",
        count: 7,
        items: [
          { Icon: FaChair, label: "Seated exercise" },
          { Icon: FaDumbbell, label: "Shoulders & arms" },
          { Icon: FaChildReaching, label: "Arms & coordination" },
          { Icon: FaPersonWalking, label: "Legs & balance" },
          { Icon: FaCouch, label: "Hips & standing up" },
          { Icon: FaChild, label: "Full-body mix" },
        ],
      },
      {
        tone: "mind",
        label: "Brain training",
        count: 9,
        items: [
          { Icon: FaBasketShopping, label: "Shopping basket memory" },
          { Icon: FaEye, label: "Shopping tray" },
          { Icon: FaTableCellsLarge, label: "Connections" },
          { Icon: FaFont, label: "Wordle" },
          { Icon: FaTableCells, label: "Crossword" },
          { Icon: FaFlag, label: "Guess the flag" },
          { Icon: FaEarthEurope, label: "Guess the country" },
          { Icon: FaTrophy, label: "Quiz show" },
        ],
      },
    ],
  },
};

const ROUTINES = {
  ...PICK.de,
  groups: [
    {
      tone: "sport",
      label: "Unterhaltung",
      count: 4,
      items: [
        { Icon: FiBookOpen, label: "Hörbuch hören" },
        { Icon: FiFileText, label: "Nachrichten" },
        { Icon: FiRadio, label: "Radio hören" },
        { Icon: FiTv, label: "Tagesschau gucken" },
      ],
    },
    {
      tone: "mind",
      label: "Gesundheit",
      count: 1,
      items: [{ Icon: FiDroplet, label: "Trinken" }],
    },
    {
      tone: "mind",
      label: "Gehirnjogging",
      count: 13,
      items: [
        { Icon: FaBrain, label: "Matheaufgaben" },
        { Icon: FaBasketShopping, label: "Einkaufskorb-Memory" },
        { Icon: FaEye, label: "Einkaufstablett" },
        { Icon: FaTableCellsLarge, label: "Verbindungen" },
        { Icon: FaFont, label: "Wordle" },
        { Icon: FaTableCells, label: "Kreuzworträtsel" },
      ],
    },
  ],
};

function Picker({ copy }) {
  return (
    <>
      <StepBar />
      <p className="phone-eyebrow">{copy.eyebrow}</p>
      <p className="phone-title">{copy.title}</p>
      <p className="phone-intro">{copy.intro}</p>

      {copy.groups.map((group) => (
        <div key={group.label} className={`phone-group phone-group--${group.tone}`}>
          <p className="phone-group-label">
            <span className="phone-dot" />
            {group.label}
            <span className="phone-rule" />
            <span className="phone-count">{group.count}</span>
          </p>
          <ul className="phone-grid">
            {group.items.map(({ Icon, label }) => (
              <li key={label} className="phone-item">
                <Tile Icon={Icon} />
                <span className="phone-label">{label}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </>
  );
}

/* ---------------------------------------------------------------- screens */
function Check() {
  return (
    <>
      <StepBar full />
      <p className="phone-eyebrow">Fast fertig</p>
      <p className="phone-title">Passt das so?</p>
      <p className="phone-intro">Du kannst alles später wieder ändern.</p>

      <div className="phone-card phone-mt">
        <div className="phone-row">
          <Tile Icon={FaTableCells} />
          <span className="phone-grow">
            <b>Kreuzworträtsel</b>
            <small>Täglich · 17:00</small>
          </span>
        </div>
        {[
          ["Aktivität", "Kreuzworträtsel"],
          ["Wann", "Täglich"],
          ["Zeiten", "17:00"],
        ].map(([k, v]) => (
          <div key={k} className="phone-kv">
            <span>{k}</span>
            <b>{v}</b>
          </div>
        ))}
      </div>

      <span className="phone-cta">
        Routine erstellen <FiCheck />
      </span>
    </>
  );
}

function Profile() {
  return (
    <>
      <div className="phone-head">
        <p className="phone-title">Profil</p>
        <span className="phone-avatar">ER</span>
      </div>

      <div className="phone-actions">
        <span className="is-primary">
          <span className="phone-icon">
            <FiVideo />
          </span>
          Video
        </span>
        <span>
          <Tile Icon={FiPhone} />
          Anruf
        </span>
        <span className="is-speaker">
          <Tile Icon={FiVolume2} />
          Durchsage
        </span>
      </div>

      <div className="phone-card phone-mt">
        <div className="phone-row">
          <Tile Icon={FiUser} />
          <span className="phone-grow">
            <b>erik rubin</b>
            <small>Profil bearbeiten</small>
            <small>Zuletzt online: 29.7.2026, 17:57</small>
          </span>
          <FiChevronRight className="phone-chevron" />
        </div>
      </div>

      <div className="phone-card phone-mt">
        <div className="phone-row">
          <span className="phone-grow">
            <b>Radio</b>
            <small>5 von 5 Sendern ausgewählt</small>
          </span>
          <span className="phone-pill">Bearbeiten</span>
        </div>
      </div>

      <TabBar active="profile" />
    </>
  );
}

const FEED_PHOTO = { backgroundImage: "url(/steffi/app/feed.webp)" };

function Feed() {
  const post = (time) => (
    <p className="phone-post">
      <span className="phone-avatar phone-avatar--sm">ER</span>
      <b>Erik Rubinov</b>
      <small>{time}</small>
    </p>
  );
  return (
    <>
      <div className="phone-feed">
        {post("19:17")}
        <span className="phone-bubble">Moin</span>

        <p className="phone-day">
          <span className="phone-rule" />
          Heute
          <span className="phone-rule" />
        </p>

        {post("11:11")}
        <span className="phone-polaroid">
          <span className="phone-photo phone-photo--family" style={FEED_PHOTO} />
        </span>

        {post("11:11")}
        <span className="phone-voice">
          <span className="phone-play">
            <FiPlay />
          </span>
          <span className="phone-wave" />
          0:02
        </span>

        {post("11:12")}
        <span className="phone-link">
          <span className="phone-photo phone-photo--cake" style={FEED_PHOTO} />
          <span>
            <small>
              <FiGlobe /> Chefkoch
            </small>
            <b>Buttermilch-Zitronentorte</b>
          </span>
        </span>
      </div>

      <div className="phone-compose">
        <span className="phone-input">Nachricht …</span>
        <span className="phone-round">
          <FiCamera />
        </span>
        <span className="phone-round phone-round--accent">
          <FiMic />
        </span>
      </div>
      <TabBar active="feed" />
    </>
  );
}

/* July 2026 starts on a Wednesday */
const CAL_DAYS = [29, 30, ...Array.from({ length: 31 }, (_, i) => i + 1), 1, 2];

function Calendar() {
  return (
    <>
      <div className="phone-head phone-head--top">
        <span>
          <p className="phone-eyebrow phone-eyebrow--flat">Kalender</p>
          <p className="phone-title">Juli 2026</p>
        </span>
        <span className="phone-pill phone-pill--accent">
          <FiPlus /> Neuer Termin
        </span>
      </div>

      <div className="phone-cal-tools">
        <span className="phone-pill">
          <FiChevronLeft /> Heute <FiChevronRight />
        </span>
        <span className="phone-seg">
          <span>Tag</span>
          <span className="is-on">Monat</span>
        </span>
      </div>

      <div className="phone-cal">
        {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((d) => (
          <small key={d}>{d}</small>
        ))}
        {CAL_DAYS.map((day, i) => {
          const outside = i < 2 || i > 32;
          const cls = outside ? "is-out" : day === 8 ? "is-picked" : day === 31 ? "is-today" : "";
          return (
            <span key={i} className={cls}>
              {day}
            </span>
          );
        })}
      </div>

      <div className="phone-card phone-mt">
        <div className="phone-kv phone-kv--first">
          <b>Mittwoch, 08. Juli</b>
          <span>Keine Termine</span>
        </div>
        <p className="phone-empty">Nichts geplant. Genieße den Tag.</p>
      </div>

      <TabBar active="calendar" />
    </>
  );
}

function Reminders() {
  return (
    <>
      <p className="phone-title phone-title--top">Erinnerungen</p>
      <p className="phone-intro">3 aktiv · 0 pausiert</p>

      <span className="phone-seg phone-seg--wide phone-mt">
        <span className="is-on">Aktiv</span>
        <span>Inaktiv</span>
      </span>

      <span className="phone-pill phone-pill--accent phone-mt">
        <FiPlus /> Neue Erinnerung
      </span>

      {[
        ["19:00", "Tabletten nehmen", "in 21 Min"],
        ["20:00", "Ofen ausmachen", "in 1 Std"],
        ["09:00", "Trinkerinnerung", "in 14 Std"],
      ].map(([time, title, due]) => (
        <div key={title} className="phone-card phone-mt">
          <div className="phone-row">
            <Tile Icon={FiBell} />
            <span className="phone-grow">
              <small>Erinnerung · {time}</small>
              <b>{title}</b>
              <small className="phone-due">{due}</small>
            </span>
            <span className="phone-toggle" />
          </div>
        </div>
      ))}

      <TabBar active="reminders" />
    </>
  );
}

function Emergency() {
  return (
    <div className="phone-sos">
      <span className="phone-sos-tag">
        <i /> Notruf · SteffiCare
      </span>
      <span className="phone-sos-rings">
        <FiUser />
      </span>
      <p className="phone-sos-name">Anna Rubin</p>
      <p className="phone-sos-line">Mutter · eingehender Notruf</p>
      <p className="phone-sos-note">
        <FiMic /> „Hilf Steffi“ am Gerät ausgelöst
      </p>
      <div className="phone-sos-actions">
        <span>
          <i className="is-decline">
            <FiPhoneOff />
          </i>
          Ablehnen
        </span>
        <span>
          <i className="is-accept">
            <FiPhone />
          </i>
          Annehmen
        </span>
      </div>
    </div>
  );
}

function Participation() {
  const changes = [
    [FiZap, "Gedächtnistraining", "5x → 2x pro Woche", "Reduziert", "down", "2,3 12,3 18,8 26,13 38,13"],
    [FiActivity, "Spaziergang", "3x → 5x pro Woche", "Gesteigert", "up", "2,13 10,13 16,7 24,7 28,3 38,3"],
    [FiZap, "Atemübungen", "seit 9 Tagen · 4x genutzt", "Neu", "new", null],
    [FiZap, "Wortspiele", "seit 12 Tagen nicht genutzt", "Gestoppt", "off", null],
  ];
  return (
    <>
      <BackRow />
      <div className="phone-head">
        <span>
          <p className="phone-eyebrow phone-eyebrow--flat">Gesundheit & Aktivität</p>
          <p className="phone-title">Überblick</p>
        </span>
        <span className="phone-avatar">ER</span>
      </div>

      <span className="phone-seg phone-seg--wide phone-mt">
        <span className="is-on">Woche</span>
        <span>Monat</span>
      </span>

      <div className="phone-card phone-mt">
        <div className="phone-row">
          <Ring value={74} />
          <span className="phone-grow">
            <small className="phone-caps">Aktivitäts-Score</small>
            <b className="phone-up">
              <FiArrowUp /> +6 Pkt. ggü. Vorperiode
            </b>
            <small>Kombiniert körperliche und mentale Übungen zu einem Gesamtbild.</small>
          </span>
        </div>
        <div className="phone-stats">
          <span>
            <small>
              <Tile Icon={FiActivity} /> Körperlich
            </small>
            <b>58</b>
            <em className="phone-down">
              <FiArrowDown /> -15%
            </em>
            <Spark points="2,3 10,6 18,11 28,14 34,10 38,11" />
          </span>
          <span>
            <small>
              <Tile Icon={FiZap} /> Mental
            </small>
            <b>86</b>
            <em className="phone-up">
              <FiArrowUp /> +22%
            </em>
            <Spark points="2,14 10,11 18,6 26,4 38,2" />
          </span>
        </div>
      </div>

      <p className="phone-caps phone-mt">Was sich verändert hat</p>
      {changes.map(([Icon, title, sub, tag, tone, points]) => (
        <div key={title} className="phone-card phone-card--tight">
          <div className="phone-row">
            <Tile Icon={Icon} />
            <span className="phone-grow">
              <b>{title}</b>
              <small>{sub}</small>
            </span>
            {points && <Spark points={points} />}
            <span className={`phone-tag phone-tag--${tone}`}>{tag}</span>
          </div>
        </div>
      ))}
    </>
  );
}

function Biomarker() {
  const rows = [
    ["Sprechtempo & Fluss", 88, "+3", "up", "Wie flüssig und gleichmäßig gesprochen wird."],
    ["Tonlage & Stimmenergie", 76, "-4", "down", "Stimmhöhe und Energie im Gespräch."],
    ["Wortfindung", 84, "+1", "flat", "Wie schnell Wörter gefunden werden."],
    ["Sprechpausen-Muster", 79, "-6", "down", "Länge und Häufigkeit von Gesprächspausen."],
    ["Satzbau & Ausdruck", 81, "-2", "flat", "Satzlänge und Komplexität im Gespräch."],
  ];
  const ARROW = { up: FiArrowUp, down: FiArrowDown, flat: FiMinus };
  return (
    <>
      <BackRow />
      <div className="phone-head">
        <p className="phone-title">Stimm-Analyse</p>
        <span className="phone-round">
          <FiX />
        </span>
      </div>

      <div className="phone-row phone-mt">
        <Ring value={82} tone="ink" />
        <span className="phone-grow">
          <span className="phone-tag phone-tag--up">Unauffällig</span>
          <small>Leichte Veränderung bei Sprechpausen · +2 ggü. letzter Woche</small>
        </span>
      </div>

      <p className="phone-caps phone-mt">5 Kategorien · 20 Biomarker</p>
      {rows.map(([title, value, delta, tone, sub]) => {
        const Arrow = ARROW[tone];
        return (
          <div key={title} className="phone-card phone-card--tight">
            <div className="phone-kv phone-kv--first">
              <b>{title}</b>
              <span>
                <b>{value}</b>
                <em className={`phone-${tone}`}>
                  <Arrow /> {delta}
                </em>
              </span>
            </div>
            <span className="phone-slider" style={{ "--v": value }} />
            <small>{sub}</small>
          </div>
        );
      })}
    </>
  );
}

/* The drawn screens are written in German only, so the English page keeps the
   real screenshots. */
export const useMockScreens = () => usePublicLang().lang === "de";

const SCREENS = {
  check: Check,
  profile: Profile,
  feed: Feed,
  calendar: Calendar,
  reminders: Reminders,
  emergency: Emergency,
  participation: Participation,
  biomarker: Biomarker,
};

/** `screen` names one of the redrawn app screens. `pick` exists in both
 *  languages; the others are German only, so callers fall back to the real
 *  screenshot elsewhere. */
export default function PhoneMock({ screen = "pick", alt, className = "", style }) {
  const { lang } = usePublicLang();
  const Screen = SCREENS[screen];

  return (
    <div className={`phone-mock ${className}`} style={style} role="img" aria-label={alt}>
      {/* the frame is a child of the sized box: `cqw` on the container itself
          would measure against the page, not the phone */}
      <div className="phone-frame" aria-hidden>
        <div className={`phone-screen phone-screen--${screen}`}>
          <div className="phone-notch" />
          <div className="phone-status">
            <span>9:41</span>
            <span className="phone-status-icons">
              <span className="phone-signal">
                <i />
                <i />
                <i />
                <i />
              </span>
              <FiWifi />
              <span className="phone-battery" />
            </span>
          </div>
          {Screen ? (
            <Screen />
          ) : (
            <Picker copy={screen === "routines" ? ROUTINES : PICK[lang] || PICK.de} />
          )}
        </div>
      </div>
    </div>
  );
}
